import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon, Edit as EditIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import InfoIcon from '../components/InfoIcon';
import { agentService } from '../services/agentService';
import { applicationDefinitionService } from '../services/applicationDefinitionService';
import { serverService } from '../services/serverService';
import { tenantService } from '../services/tenantService';
import { OSType, AgentCapability, SqlServerType, capabilityHelper, type CapabilityApplicationDefinition } from '../types/agent';
import { ApplicationType, applicationDefinitionHelper, type ApplicationDefinition } from '../types/applicationDefinition';
import type { Server } from '../types/server';
import type { Tenant } from '../types/tenant';

interface DiskThreshold {
  drive: string;
  warning: number;
  critical: number;
}

const AgentForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { openTab?: number; focusField?: string } | undefined;
  const [createdAgentId, setCreatedAgentId] = useState<string | null>(null);
  const isEdit = !!id || !!createdAgentId;
  const [activeTab, setActiveTab] = useState(0);
  const bankServiceRunIntervalSecondsInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    serverId: '',
    tenantId: '',
    agentVersion: '',
    osType: OSType.Windows,
    capabilities: '{}',
    configVersion: 1,
    heartbeatIntervalSeconds: 60,
    configPullIntervalSeconds: 60,
    healthCheckIntervalSeconds: 60,
    metricsCollectionIntervalSeconds: 60,
    heartbeatTimeoutMultiplier: 2.0,
    cpuWarningThreshold: undefined as number | undefined,
    cpuCriticalThreshold: undefined as number | undefined,
    ramWarningThreshold: undefined as number | undefined,
    ramCriticalThreshold: undefined as number | undefined,
    isActive: true, // Varsayılan olarak aktif
    // eStatement bağlantı ayarları
    eStatementConnectionString: '',
    eStatementSqlServerType: undefined as SqlServerType | undefined,
    eStatementSonIslemZamaniIntervalSeconds: undefined as number | undefined,
    eStatementSirketlerCekmeSikligiSeconds: undefined as number | undefined,
    bankServiceRunIntervalSeconds: undefined as number | undefined,
    eStatementDataProtectionEnabled: false,
    eStatementDataProtectionKeyPath: '',
    runServiceAdminPasswd: '',
  });
  const [selectedCapabilities, setSelectedCapabilities] = useState<AgentCapability[]>([]);
  const [diskThresholds, setDiskThresholds] = useState<DiskThreshold[]>([]);
  
  // Her capability için ApplicationDefinition listeleri
  const [iisApps, setIisApps] = useState<CapabilityApplicationDefinition[]>([]);
  const [windowsTasks, setWindowsTasks] = useState<CapabilityApplicationDefinition[]>([]);
  const [windowsServices, setWindowsServices] = useState<CapabilityApplicationDefinition[]>([]);
  const [linuxCrons, setLinuxCrons] = useState<CapabilityApplicationDefinition[]>([]);

  const [servers, setServers] = useState<Server[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredServers, setFilteredServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingAgent, setIsLoadingAgent] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null);
  const [copiedAgentId, setCopiedAgentId] = useState(false);
  const initialAgentVersionRef = useRef<string | null>(null);
  const skipVersionReminderCheckRef = useRef(false);
  const [versionReminderDialogOpen, setVersionReminderDialogOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Popup state'leri
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState<AgentCapability | null>(null);
  const [editingApp, setEditingApp] = useState<CapabilityApplicationDefinition | null>(null);
  const hasFocusedBankServiceRunIntervalRef = useRef(false);

  useEffect(() => {
    loadInitialData();
    if (isEdit && (id || createdAgentId)) {
      loadAgent(id || createdAgentId!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, createdAgentId]);

  useEffect(() => {
    if (!loadingData && locationState?.openTab != null && (id || createdAgentId)) {
      setActiveTab(locationState.openTab);
    }
  }, [loadingData, locationState?.openTab, id, createdAgentId]);

  useEffect(() => {
    if (
      activeTab === 3 &&
      locationState?.focusField === 'bankServiceRunIntervalSeconds' &&
      !hasFocusedBankServiceRunIntervalRef.current
    ) {
      hasFocusedBankServiceRunIntervalRef.current = true;
      const t = setTimeout(() => {
        bankServiceRunIntervalSecondsInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [activeTab, locationState?.focusField]);

  useEffect(() => {
    if (formData.tenantId) {
      const filtered = servers.filter((s) => s.tenantId === formData.tenantId);
      setFilteredServers(filtered);
      if (filtered.length > 0 && !formData.serverId) {
        setFormData((prev) => ({ ...prev, serverId: filtered[0].id }));
      }
    } else {
      setFilteredServers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tenantId, servers]);

  // OS tipi değiştiğinde capabilities'i güncelle
  useEffect(() => {
    const availableCapabilities = capabilityHelper.getCapabilitiesForOS(formData.osType);
    const filtered = selectedCapabilities.filter((cap) =>
      availableCapabilities.includes(cap)
    );
    setSelectedCapabilities(filtered);
    const json = capabilityHelper.capabilitiesToJson(filtered);
    setFormData((prev) => ({ ...prev, capabilities: json }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.osType]);

  // Capabilities değiştiğinde JSON'u güncelle
  useEffect(() => {
    const json = capabilityHelper.capabilitiesToJson(selectedCapabilities);
    setFormData((prev) => ({ ...prev, capabilities: json }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCapabilities]);

  const loadInitialData = async () => {
    try {
      const [serversData, tenantsData] = await Promise.all([
        serverService.getAll(),
        tenantService.getAll(),
      ]);
      setServers(serversData);
      setTenants(tenantsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken bir hata oluştu');
    }
  };

  const loadAgent = async (agentId: string) => {
    // Eğer zaten yükleniyorsa, tekrar yükleme
    if (isLoadingAgent) {
      console.log('Agent zaten yükleniyor, atlanıyor...');
      return;
    }
    
    try {
      setIsLoadingAgent(true);
      setLoadingData(true);
      setError(null);
      
      // State'leri temizle
      setIisApps([]);
      setWindowsTasks([]);
      setWindowsServices([]);
      setLinuxCrons([]);
      
      const agent = await agentService.getById(agentId);
      console.log('Agent yüklendi:', agent);
      
      // AgentId'yi sakla
      setCurrentAgentId(agent.id);
      
      // OSType'ı doğru şekilde map et (backend'den number olarak geliyor)
      const osType = typeof agent.osType === 'number' 
        ? agent.osType as OSType 
        : (agent.osTypeName === 'Windows' ? OSType.Windows : OSType.Linux);
      
      // eStatementSqlServerType'ı doğru şekilde map et (backend'den number olarak geliyor)
      let eStatementSqlServerType: SqlServerType | undefined = undefined;
      if (agent.eStatementSqlServerType !== undefined && agent.eStatementSqlServerType !== null) {
        if (typeof agent.eStatementSqlServerType === 'number') {
          eStatementSqlServerType = agent.eStatementSqlServerType as SqlServerType;
        } else {
          // String olarak gelirse enum'a çevir
          const typeStr = String(agent.eStatementSqlServerType).toUpperCase();
          if (typeStr === 'MSSQL' || typeStr === '1') {
            eStatementSqlServerType = SqlServerType.MSSQL;
          } else if (typeStr === 'POSTGRESQL' || typeStr === '2') {
            eStatementSqlServerType = SqlServerType.POSTGRESQL;
          } else if (typeStr === 'MYSQL' || typeStr === '3') {
            eStatementSqlServerType = SqlServerType.MYSQL;
          }
        }
      }
      
      const capabilities = capabilityHelper.jsonToCapabilities(agent.capabilities);
      console.log('Capabilities parse edildi:', capabilities, 'JSON:', agent.capabilities);
      console.log('eStatementSqlServerType mapped:', eStatementSqlServerType, 'from:', agent.eStatementSqlServerType);

      initialAgentVersionRef.current = agent.agentVersion ?? null;

      setFormData({
        serverId: agent.serverId,
        tenantId: agent.tenantId,
        agentVersion: agent.agentVersion,
        osType: osType,
        capabilities: agent.capabilities,
        configVersion: agent.configVersion,
        heartbeatIntervalSeconds: agent.heartbeatIntervalSeconds || 60,
        configPullIntervalSeconds: agent.configPullIntervalSeconds || 60,
        healthCheckIntervalSeconds: agent.healthCheckIntervalSeconds || 60,
        metricsCollectionIntervalSeconds: agent.metricsCollectionIntervalSeconds || 60,
        heartbeatTimeoutMultiplier: agent.heartbeatTimeoutMultiplier || 2.0,
        cpuWarningThreshold: agent.cpuWarningThreshold,
        cpuCriticalThreshold: agent.cpuCriticalThreshold,
        ramWarningThreshold: agent.ramWarningThreshold,
        ramCriticalThreshold: agent.ramCriticalThreshold,
        isActive: agent.isActive ?? true,
        eStatementConnectionString: agent.eStatementConnectionString || '',
        eStatementSqlServerType: eStatementSqlServerType,
        eStatementSonIslemZamaniIntervalSeconds: agent.eStatementSonIslemZamaniIntervalSeconds,
        eStatementSirketlerCekmeSikligiSeconds: agent.eStatementSirketlerCekmeSikligiSeconds,
        bankServiceRunIntervalSeconds: agent.bankServiceRunIntervalSeconds,
        eStatementDataProtectionEnabled: agent.eStatementDataProtectionEnabled ?? false,
        eStatementDataProtectionKeyPath: agent.eStatementDataProtectionKeyPath ?? '',
        runServiceAdminPasswd: agent.runServiceAdminPasswd ?? '',
      });
      setSelectedCapabilities(capabilities);
      
      // Disk threshold'larını yükle
      try {
        const diskThresholdsData = agent.diskThresholds ? JSON.parse(agent.diskThresholds) : [];
        setDiskThresholds(Array.isArray(diskThresholdsData) ? diskThresholdsData : []);
      } catch {
        setDiskThresholds([]);
      }

      // Agent'ın ApplicationDefinition'larını yükle
      try {
        const appDefs = await applicationDefinitionService.getAll(agent.tenantId, agent.serverId);
        console.log('ApplicationDefinition\'lar yüklendi:', appDefs);
        
        // State'leri temizle ve yeni değerleri topla
        const newIisApps: CapabilityApplicationDefinition[] = [];
        const newWindowsTasks: CapabilityApplicationDefinition[] = [];
        const newWindowsServices: CapabilityApplicationDefinition[] = [];
        const newLinuxCrons: CapabilityApplicationDefinition[] = [];
        
        // Capability'lere göre grupla
        appDefs.forEach((app) => {
          try {
            console.log('ApplicationDefinition işleniyor:', app);
            
            // ApplicationType'ı doğru şekilde map et
            const appType = typeof app.applicationType === 'number' 
              ? app.applicationType as ApplicationType
              : (app.applicationTypeName === 'IisApp' ? ApplicationType.IisApp
                : app.applicationTypeName === 'WindowsTask' ? ApplicationType.WindowsTask
                : app.applicationTypeName === 'WindowsService' ? ApplicationType.WindowsService
                : ApplicationType.LinuxCron);
            
            const appDef: CapabilityApplicationDefinition = {
              id: app.id,
              name: app.name,
              expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
            };

            // Identifier'ı parse et
            if (app.identifier && app.identifier.trim() !== '' && app.identifier !== '{}') {
              try {
                // Backend'den gelen format: {"type":"iis-app","identifier":{...}}
                // veya direkt identifier objesi olabilir
                let identifierData: any = null;
                try {
                  const parsed = JSON.parse(app.identifier);
                  // Eğer {type: '...', identifier: {...}} formatındaysa
                  if (parsed && typeof parsed === 'object' && 'identifier' in parsed) {
                    identifierData = parsed.identifier;
                  } else {
                    // Direkt identifier objesi
                    identifierData = parsed;
                  }
                } catch {
                  // JSON parse edilemezse, helper'ı kullan
                  identifierData = applicationDefinitionHelper.jsonToIdentifier(app.identifier, appType);
                }
                
                console.log('Identifier parse edildi:', identifierData, 'JSON:', app.identifier);
                
                if (identifierData && typeof identifierData === 'object') {
                  if ('siteName' in identifierData) {
                    // IIS App
                    appDef.siteName = identifierData.siteName || '';
                    appDef.appPoolName = identifierData.appPoolName || '';
                    appDef.expectedSiteState = identifierData.expectedSiteState || 'Started';
                    appDef.expectedAppPoolState = identifierData.expectedAppPoolState || 'Running';
                    appDef.healthCheckUrl = identifierData.healthCheckUrl || '';
                    appDef.httpMethod = identifierData.httpMethod || 'GET';
                    appDef.httpStatusCodeExpected = identifierData.httpStatusCodeExpected || 200;
                    appDef.responseTimeThresholdMs = identifierData.responseTimeThresholdMs;
                    appDef.timeoutMs = identifierData.timeoutMs;
                  } else if ('taskName' in identifierData) {
                    // Windows Task
                    appDef.taskName = identifierData.taskName || '';
                    appDef.lastRunResultExpected = identifierData.lastRunResultExpected;
                    appDef.shouldBeRunning = identifierData.shouldBeRunning;
                  } else if ('serviceName' in identifierData) {
                    // Windows Service
                    appDef.serviceName = identifierData.serviceName || '';
                    appDef.expectedStatus = identifierData.expectedStatus || 'Running';
                    appDef.autoRestartEnabled = identifierData.autoRestartEnabled;
                    appDef.restartAttemptCount = identifierData.restartAttemptCount;
                  } else if ('jobName' in identifierData) {
                    // Linux Cron
                    appDef.jobName = identifierData.jobName || '';
                    appDef.schedule = identifierData.schedule || '';
                    appDef.lastExecutionStatusExpected = identifierData.lastExecutionStatusExpected;
                  }
                }
              } catch (identifierErr) {
                console.error('Identifier parse hatası:', identifierErr, 'JSON:', app.identifier);
              }
            } else {
              console.warn('Identifier boş veya geçersiz:', app.identifier);
            }

            // HealthRule'ı parse et
            if (app.healthRule) {
              try {
                const healthRule = applicationDefinitionHelper.jsonToHealthRule(app.healthRule);
                if (healthRule && 'maxDelayMinutes' in healthRule) {
                  appDef.maxDelayMinutes = healthRule.maxDelayMinutes;
                }
              } catch (healthRuleErr) {
                console.error('HealthRule parse hatası:', healthRuleErr, 'JSON:', app.healthRule);
              }
            }

            // ApplicationType'a göre ilgili array'e ekle
            console.log('ApplicationDefinition eklenecek:', appDef, 'Type:', appType);
            if (appType === ApplicationType.IisApp) {
              newIisApps.push(appDef);
            } else if (appType === ApplicationType.WindowsTask) {
              newWindowsTasks.push(appDef);
            } else if (appType === ApplicationType.WindowsService) {
              newWindowsServices.push(appDef);
            } else if (appType === ApplicationType.LinuxCron) {
              newLinuxCrons.push(appDef);
            }
          } catch (appErr: any) {
            console.error(`ApplicationDefinition parse hatası (${app.id}):`, appErr);
            // Hatalı ApplicationDefinition'ı atla, devam et
          }
        });
        
        // State'leri toplu olarak güncelle
        setIisApps(newIisApps);
        setWindowsTasks(newWindowsTasks);
        setWindowsServices(newWindowsServices);
        setLinuxCrons(newLinuxCrons);
        
        console.log('ApplicationDefinition\'lar işlendi. IIS:', newIisApps.length, 'Task:', newWindowsTasks.length, 'Service:', newWindowsServices.length, 'Cron:', newLinuxCrons.length);
      } catch (appDefErr: any) {
        console.error('ApplicationDefinition yükleme hatası:', appDefErr);
        // ApplicationDefinition yükleme hatası kritik değil, sadece logla
        // Agent bilgileri yüklendi, form açılabilir
      }
    } catch (err: any) {
      console.error('Agent yükleme hatası:', err);
      setError(err.response?.data?.message || err.message || 'Agent yüklenirken bir hata oluştu');
    } finally {
      setLoadingData(false);
      setIsLoadingAgent(false);
    }
  };

  const handleOpenDialog = (capability: AgentCapability, app?: CapabilityApplicationDefinition) => {
    setSelectedCapability(capability);
    if (app) {
      // Düzenleme modu
      setEditingApp({ ...app });
    } else {
      // Yeni ekleme modu
      const newApp: CapabilityApplicationDefinition = {
        id: `temp-${Date.now()}-${Math.random()}`,
        name: '',
        // Default values based on capability
        ...(capability === AgentCapability.IisMonitoring && {
          siteName: '',
          appPoolName: '',
          expectedSiteState: 'Started' as const,
          expectedAppPoolState: 'Running' as const,
          httpMethod: 'GET',
          httpStatusCodeExpected: 200,
        }),
        ...(capability === AgentCapability.WindowsTaskMonitoring && {
          taskName: '',
          maxDelayMinutes: 15,
          shouldBeRunning: false,
        }),
        ...(capability === AgentCapability.WindowsServiceMonitoring && {
          serviceName: '',
          expectedStatus: 'Running' as const,
          autoRestartEnabled: false,
        }),
        ...(capability === AgentCapability.LinuxCronMonitoring && {
          jobName: '',
          schedule: '',
          maxDelayMinutes: 15,
        }),
      };
      setEditingApp(newApp);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCapability(null);
    setEditingApp(null);
  };

  const handleSaveApp = () => {
    if (!selectedCapability || !editingApp) return;

    // Validation
    let isValid = false;
    if (selectedCapability === AgentCapability.IisMonitoring) {
      isValid = !!(editingApp.name && editingApp.name.trim() !== '' &&
        editingApp.siteName !== undefined && editingApp.siteName !== null && 
        editingApp.appPoolName !== undefined && editingApp.appPoolName !== null && 
        editingApp.expectedSiteState && editingApp.expectedSiteState.trim() !== '' &&
        editingApp.expectedAppPoolState && editingApp.expectedAppPoolState.trim() !== '');
    } else if (selectedCapability === AgentCapability.WindowsTaskMonitoring) {
      isValid = !!(editingApp.name && editingApp.name.trim() !== '' && 
        editingApp.taskName && editingApp.taskName.trim() !== '' && 
        editingApp.maxDelayMinutes && editingApp.maxDelayMinutes > 0);
    } else if (selectedCapability === AgentCapability.WindowsServiceMonitoring) {
      isValid = !!(editingApp.name && editingApp.name.trim() !== '' && 
        editingApp.serviceName && editingApp.serviceName.trim() !== '' && 
        editingApp.expectedStatus && editingApp.expectedStatus.trim() !== '');
    } else if (selectedCapability === AgentCapability.LinuxCronMonitoring) {
      isValid = !!(editingApp.name && editingApp.name.trim() !== '' && 
        editingApp.jobName && editingApp.jobName.trim() !== '' && 
        editingApp.schedule && editingApp.schedule.trim() !== '' && 
        editingApp.maxDelayMinutes && editingApp.maxDelayMinutes > 0);
    }

    if (!isValid) {
      setError('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    // Eğer app.id varsa ve temp- ile başlamıyorsa güncelleme, yoksa yeni ekleme
    if (editingApp.id && !editingApp.id.startsWith('temp-')) {
      // Güncelleme - tüm alanları güncelle
      const apps = selectedCapability === AgentCapability.IisMonitoring
        ? iisApps
        : selectedCapability === AgentCapability.WindowsTaskMonitoring
        ? windowsTasks
        : selectedCapability === AgentCapability.WindowsServiceMonitoring
        ? windowsServices
        : linuxCrons;
      
      const appIndex = apps.findIndex((a) => a.id === editingApp.id);
      if (appIndex !== -1) {
        const updater = (prev: CapabilityApplicationDefinition[]) =>
          prev.map((a, idx) => (idx === appIndex ? editingApp : a));
        
        if (selectedCapability === AgentCapability.IisMonitoring) {
          setIisApps(updater);
        } else if (selectedCapability === AgentCapability.WindowsTaskMonitoring) {
          setWindowsTasks(updater);
        } else if (selectedCapability === AgentCapability.WindowsServiceMonitoring) {
          setWindowsServices(updater);
        } else if (selectedCapability === AgentCapability.LinuxCronMonitoring) {
          setLinuxCrons(updater);
        }
      }
    } else {
      // Yeni ekleme
      if (selectedCapability === AgentCapability.IisMonitoring) {
        setIisApps((prev) => [...prev, editingApp]);
      } else if (selectedCapability === AgentCapability.WindowsTaskMonitoring) {
        setWindowsTasks((prev) => [...prev, editingApp]);
      } else if (selectedCapability === AgentCapability.WindowsServiceMonitoring) {
        setWindowsServices((prev) => [...prev, editingApp]);
      } else if (selectedCapability === AgentCapability.LinuxCronMonitoring) {
        setLinuxCrons((prev) => [...prev, editingApp]);
      }
    }

    handleCloseDialog();
  };

  const removeApplicationDefinition = (capability: AgentCapability, appId?: string) => {
    if (!appId) return;
    if (capability === AgentCapability.IisMonitoring) {
      setIisApps((prev) => prev.filter((app) => app.id !== appId));
    } else if (capability === AgentCapability.WindowsTaskMonitoring) {
      setWindowsTasks((prev) => prev.filter((app) => app.id !== appId));
    } else if (capability === AgentCapability.WindowsServiceMonitoring) {
      setWindowsServices((prev) => prev.filter((app) => app.id !== appId));
    } else if (capability === AgentCapability.LinuxCronMonitoring) {
      setLinuxCrons((prev) => prev.filter((app) => app.id !== appId));
    }
  };


  // Capability açıklamaları (CapabilityDocs.md'ye göre)
  const getCapabilityDescription = (capability: AgentCapability): string => {
    const descriptions: Record<AgentCapability, string> = {
      [AgentCapability.CpuMonitoring]: 'Sunucunun işlemci kullanımını izler. Genel sistem sağlığı için temel metriktir, application bağımsızdır.',
      [AgentCapability.MemoryMonitoring]: 'Fiziksel ve kullanılabilir bellek durumunu izler. Memory leak veya aşırı yük durumlarını erken yakalamak için kullanılır.',
      [AgentCapability.DiskMonitoring]: 'Disk doluluk ve disk erişim durumlarını izler. Disk doluluğu çoğu sistemde kritik hatalara sebep olur, mutlaka izlenmelidir.',
      [AgentCapability.IisMonitoring]: 'IIS üzerinde çalışan web uygulamalarını izler. Web uygulaması özelinde monitoring yapılmasını sağlar.',
      [AgentCapability.WindowsTaskMonitoring]: 'Windows Task Scheduler üzerinden çalışan görevleri izler. Batch job, entegrasyon veya gece çalışan işler için kritik öneme sahiptir.',
      [AgentCapability.WindowsServiceMonitoring]: 'Windows servislerinin durumunu izler. Arka planda çalışan servis tabanlı uygulamalar için zorunludur.',
      [AgentCapability.LinuxCronMonitoring]: 'Linux cron job\'larını izler. Windows Task Scheduler\'ın Linux karşılığıdır.',
    };
    return descriptions[capability] || '';
  };

  // Parametre açıklamaları (CapabilityDocs V2'ye göre)
  const getParameterDescription = (capability: AgentCapability, field: string): string => {
    const descriptions: Record<string, Record<string, string>> = {
      [AgentCapability.IisMonitoring]: {
        siteName: 'İzlenecek IIS Web Site adı. Zorunlu alan.',
        appPoolName: 'Worker process\'in bağlı olduğu Application Pool adı. Zorunlu alan.',
        expectedSiteState: 'IIS Site\'ın beklenen durumu. Genellikle Started. Zorunlu alan.',
        expectedAppPoolState: 'Application Pool\'un beklenen durumu. Genellikle Running. Zorunlu alan.',
        healthCheckUrl: 'Application seviyesinde kontrol için URL. Varsa gerçek application health izlenir, yoksa sadece IIS yapı sağlığı izlenir. Opsiyonel.',
        httpMethod: 'Health check için kullanılacak HTTP metodu. Varsayılan: GET. Opsiyonel.',
        httpStatusCodeExpected: 'Beklenen HTTP response code. Örn: 200. Opsiyonel.',
        responseTimeThresholdMs: 'Yanıt süresi eşik değeri (milisaniye). Yavaşlık tespiti için. Opsiyonel.',
        timeoutMs: 'Request\'in maksimum bekleme süresi (milisaniye). Opsiyonel.',
      },
      [AgentCapability.WindowsTaskMonitoring]: {
        taskName: 'Task Scheduler\'daki görev adı. Zorunlu alan.',
        maxDelayMinutes: 'Planlanan çalışmaya göre kabul edilebilir gecikme süresi (dakika). Zorunlu alan.',
        lastRunResultExpected: 'Başarılı çalışmanın exit code karşılığı. Opsiyonel.',
        shouldBeRunning: 'Sürekli çalışması gereken task\'lar için. Opsiyonel.',
      },
      [AgentCapability.WindowsServiceMonitoring]: {
        serviceName: 'Windows Service adı (Service Name, Display Name değil). Zorunlu alan.',
        expectedStatus: 'Beklenen servis durumu. Genellikle Running. Zorunlu alan.',
        autoRestartEnabled: 'Agent\'ın servisi yeniden başlatmasına izin verilip verilmediği. Opsiyonel.',
        restartAttemptCount: 'Maksimum yeniden başlatma denemesi. Opsiyonel.',
      },
      [AgentCapability.LinuxCronMonitoring]: {
        jobName: 'Cron job adı. Zorunlu alan.',
        schedule: 'Cron schedule formatı. Örn: "0 */2 * * *" (her 2 saatte bir). Zorunlu alan.',
        maxDelayMinutes: 'Planlanan çalışmaya göre kabul edilebilir gecikme süresi (dakika). Zorunlu alan.',
        lastExecutionStatusExpected: 'Başarılı çalışmanın exit code karşılığı. Opsiyonel.',
      },
    };
    return descriptions[capability]?.[field] || '';
  };

  const renderApplicationDefinitionForm = (capability: AgentCapability) => {
    const apps =
      capability === AgentCapability.IisMonitoring
        ? iisApps
        : capability === AgentCapability.WindowsTaskMonitoring
        ? windowsTasks
        : capability === AgentCapability.WindowsServiceMonitoring
        ? windowsServices
        : capability === AgentCapability.LinuxCronMonitoring
        ? linuxCrons
        : [];

    const getTableHeaders = () => {
      if (capability === AgentCapability.IisMonitoring) {
        return ['Uygulama Adı', 'Site Name', 'App Pool Name', 'İşlemler'];
      } else if (capability === AgentCapability.WindowsTaskMonitoring) {
        return ['Uygulama Adı', 'Task Name', 'Max Delay (dk)', 'İşlemler'];
      } else if (capability === AgentCapability.WindowsServiceMonitoring) {
        return ['Uygulama Adı', 'Service Name', 'Expected Status', 'İşlemler'];
      } else if (capability === AgentCapability.LinuxCronMonitoring) {
        return ['Uygulama Adı', 'Job Name', 'Schedule', 'İşlemler'];
      }
      return ['Uygulama Adı', 'İşlemler'];
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Bu capability için izlenecek uygulamaları ekleyin
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(capability)}
            sx={{ minWidth: 'auto' }}
          >
            Ekle
          </Button>
        </Box>

        {apps.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Henüz uygulama tanımı eklenmedi
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => handleOpenDialog(capability)}
            >
              İlk uygulamayı eklemek için tıklayın
            </Button>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                  {getTableHeaders().map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.map((app) => (
                  <TableRow
                    key={app.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDialog(capability, app)}
                  >
                    <TableCell>{app.name || '-'}</TableCell>
                    {capability === AgentCapability.IisMonitoring && (
                      <>
                        <TableCell>{app.siteName || '-'}</TableCell>
                        <TableCell>{app.appPoolName || '-'}</TableCell>
                      </>
                    )}
                    {capability === AgentCapability.WindowsTaskMonitoring && (
                      <>
                        <TableCell>{app.taskName || '-'}</TableCell>
                        <TableCell>{app.maxDelayMinutes || '-'}</TableCell>
                      </>
                    )}
                    {capability === AgentCapability.WindowsServiceMonitoring && (
                      <>
                        <TableCell>{app.serviceName || '-'}</TableCell>
                        <TableCell>{app.expectedStatus || '-'}</TableCell>
                      </>
                    )}
                    {capability === AgentCapability.LinuxCronMonitoring && (
                      <>
                        <TableCell>{app.jobName || '-'}</TableCell>
                        <TableCell>{app.schedule || '-'}</TableCell>
                      </>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(capability, app)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeApplicationDefinition(capability, app.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderDialogForm = () => {
    if (!selectedCapability || !editingApp) return null;

    // Non-null assertion - zaten yukarıda null check yapıldı
    const app = editingApp;
    const capability = selectedCapability;

    const handleFieldChange = <K extends keyof CapabilityApplicationDefinition>(
      field: K,
      value: CapabilityApplicationDefinition[K]
    ) => {
      setEditingApp({ ...app, [field]: value } as CapabilityApplicationDefinition);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {/* Common: Uygulama Adı */}
        <TextField
          fullWidth
          size="small"
          label="Uygulama Adı"
          value={app.name || ''}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          placeholder="Örn: MyWebApp"
          required
        />

        {/* IIS Monitoring Fields */}
        {capability === AgentCapability.IisMonitoring && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Site Name <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'siteName')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.siteName || ''}
                  onChange={(e) => handleFieldChange('siteName', e.target.value)}
                  placeholder="Örn: MyWebApp"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    App Pool Name <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'appPoolName')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.appPoolName || ''}
                  onChange={(e) => handleFieldChange('appPoolName', e.target.value)}
                  placeholder="Örn: MyWebAppPool"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Expected Site State <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'expectedSiteState')} />
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    value={app.expectedSiteState || 'Started'}
                    onChange={(e) => handleFieldChange('expectedSiteState', e.target.value as 'Started' | 'Stopped')}
                  >
                    <MenuItem value="Started">Started</MenuItem>
                    <MenuItem value="Stopped">Stopped</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Expected App Pool State <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'expectedAppPoolState')} />
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    value={app.expectedAppPoolState || 'Running'}
                    onChange={(e) => handleFieldChange('expectedAppPoolState', e.target.value as 'Running' | 'Stopped')}
                  >
                    <MenuItem value="Running">Running</MenuItem>
                    <MenuItem value="Stopped">Stopped</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Health Check URL
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'healthCheckUrl')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.healthCheckUrl || ''}
                  onChange={(e) => handleFieldChange('healthCheckUrl', e.target.value)}
                  placeholder="Örn: https://mysite.com/health"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    HTTP Method
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'httpMethod')} />
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    value={app.httpMethod || 'GET'}
                    onChange={(e) => handleFieldChange('httpMethod', e.target.value)}
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="HEAD">HEAD</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Expected HTTP Status Code
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'httpStatusCodeExpected')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={app.httpStatusCodeExpected || 200}
                  onChange={(e) => handleFieldChange('httpStatusCodeExpected', e.target.value ? Number(e.target.value) : 200)}
                  placeholder="200"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Response Time Threshold (ms)
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'responseTimeThresholdMs')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={app.responseTimeThresholdMs || ''}
                  onChange={(e) => handleFieldChange('responseTimeThresholdMs', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="Örn: 5000"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Timeout (ms)
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'timeoutMs')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={app.timeoutMs || ''}
                  onChange={(e) => handleFieldChange('timeoutMs', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="Örn: 30000"
                />
              </Box>
            </Box>
          </>
        )}

        {/* Windows Task Monitoring Fields */}
        {capability === AgentCapability.WindowsTaskMonitoring && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Task Name <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'taskName')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.taskName || ''}
                  onChange={(e) => handleFieldChange('taskName', e.target.value)}
                  placeholder="Örn: BankFetchJob"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Max Delay (Dakika) <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'maxDelayMinutes')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={app.maxDelayMinutes || ''}
                  onChange={(e) => handleFieldChange('maxDelayMinutes', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="15"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Last Run Result Expected
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'lastRunResultExpected')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.lastRunResultExpected || ''}
                  onChange={(e) => handleFieldChange('lastRunResultExpected', e.target.value)}
                  placeholder="Örn: 0 (Success)"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={app.shouldBeRunning || false}
                      onChange={(e) => handleFieldChange('shouldBeRunning', e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">Should Be Running</Typography>
                      <InfoIcon text={getParameterDescription(capability, 'shouldBeRunning')} />
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Beklenen Çalışma Aralığı (Dakika)
                  </Typography>
                  <InfoIcon text="Uygulamanın ne sıklıkla çalışması beklendiğini belirtir. Örn: Her 60 dakikada bir çalışan bir task için 60 yazılabilir. Bu bilgi dashboard ve alerting sisteminde kullanılır." />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={app.expectedRunIntervalMinutes || ''}
                  onChange={(e) => handleFieldChange('expectedRunIntervalMinutes', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="Örn: 60"
                />
              </Box>
            </Box>
          </>
        )}

        {/* Windows Service Monitoring Fields */}
        {capability === AgentCapability.WindowsServiceMonitoring && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Service Name <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'serviceName')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.serviceName || ''}
                  onChange={(e) => handleFieldChange('serviceName', e.target.value)}
                  placeholder="Örn: MyWorkerService"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Expected Status
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'expectedStatus')} />
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    value={app.expectedStatus || 'Running'}
                    onChange={(e) => handleFieldChange('expectedStatus', e.target.value as 'Running' | 'Stopped')}
                  >
                    <MenuItem value="Running">Running</MenuItem>
                    <MenuItem value="Stopped">Stopped</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={app.autoRestartEnabled || false}
                      onChange={(e) => handleFieldChange('autoRestartEnabled', e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">Auto Restart Enabled</Typography>
                      <InfoIcon text={getParameterDescription(capability, 'autoRestartEnabled')} />
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Restart Attempt Count
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'restartAttemptCount')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={app.restartAttemptCount || ''}
                  onChange={(e) => handleFieldChange('restartAttemptCount', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="Örn: 3"
                />
              </Box>
            </Box>
          </>
        )}

        {/* Linux Cron Monitoring Fields */}
        {capability === AgentCapability.LinuxCronMonitoring && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Job Name <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'jobName')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.jobName || ''}
                  onChange={(e) => handleFieldChange('jobName', e.target.value)}
                  placeholder="Örn: BackupJob"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Schedule (Cron) <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'schedule')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.schedule || ''}
                  onChange={(e) => handleFieldChange('schedule', e.target.value)}
                  placeholder="Örn: 0 */2 * * *"
                  required
                  helperText="Cron format: minute hour day month weekday"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Max Delay (Dakika) <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'maxDelayMinutes')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={app.maxDelayMinutes || ''}
                  onChange={(e) => handleFieldChange('maxDelayMinutes', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="15"
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Last Execution Status Expected
                  </Typography>
                  <InfoIcon text={getParameterDescription(capability, 'lastExecutionStatusExpected')} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={app.lastExecutionStatusExpected || ''}
                  onChange={(e) => handleFieldChange('lastExecutionStatusExpected', e.target.value)}
                  placeholder="Örn: 0 (Success)"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Beklenen Çalışma Aralığı (Dakika)
                  </Typography>
                  <InfoIcon text="Uygulamanın ne sıklıkla çalışması beklendiğini belirtir. Örn: Her 60 dakikada bir çalışan bir task için 60 yazılabilir. Bu bilgi dashboard ve alerting sisteminde kullanılır." />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={app.expectedRunIntervalMinutes || ''}
                  onChange={(e) => handleFieldChange('expectedRunIntervalMinutes', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="Örn: 60"
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedCapabilities.length === 0) {
      setError('En az bir capability seçmelisiniz');
      return;
    }

    const agentIdForUpdate = id || currentAgentId;
    const versionUnchanged =
      initialAgentVersionRef.current !== null &&
      String(formData.agentVersion).trim() === String(initialAgentVersionRef.current).trim();

    if (!skipVersionReminderCheckRef.current && isEdit && agentIdForUpdate && versionUnchanged) {
      skipVersionReminderCheckRef.current = false;
      setVersionReminderDialogOpen(true);
      return;
    }
    skipVersionReminderCheckRef.current = false;

    setLoading(true);
    setError(null);

    const MIN_INTERVAL_SECONDS = 60;
    const intervalErrors: string[] = [];
    if (formData.heartbeatIntervalSeconds < MIN_INTERVAL_SECONDS) intervalErrors.push('Heartbeat interval en az 60 saniye olmalıdır.');
    if (formData.configPullIntervalSeconds < MIN_INTERVAL_SECONDS) intervalErrors.push('Config pull interval en az 60 saniye olmalıdır.');
    if (formData.healthCheckIntervalSeconds < MIN_INTERVAL_SECONDS) intervalErrors.push('Health check interval en az 60 saniye olmalıdır.');
    if (formData.metricsCollectionIntervalSeconds < MIN_INTERVAL_SECONDS) intervalErrors.push('Metrics collection interval en az 60 saniye olmalıdır.');
    if (formData.eStatementSonIslemZamaniIntervalSeconds != null && formData.eStatementSonIslemZamaniIntervalSeconds > 0 && formData.eStatementSonIslemZamaniIntervalSeconds < MIN_INTERVAL_SECONDS) {
      intervalErrors.push('Son işlem zamanı çekme sıklığı en az 60 saniye olmalıdır.');
    }
    if (formData.eStatementSirketlerCekmeSikligiSeconds != null && formData.eStatementSirketlerCekmeSikligiSeconds > 0 && formData.eStatementSirketlerCekmeSikligiSeconds < MIN_INTERVAL_SECONDS) {
      intervalErrors.push('Şirketler çekme sıklığı en az 60 saniye olmalıdır.');
    }
    if (formData.bankServiceRunIntervalSeconds != null && formData.bankServiceRunIntervalSeconds > 0 && formData.bankServiceRunIntervalSeconds < MIN_INTERVAL_SECONDS) {
      intervalErrors.push('Banka servis tetikleme sıklığı en az 60 saniye olmalıdır.');
    }
    if (intervalErrors.length > 0) {
      setError(intervalErrors.join(' '));
      return;
    }

    try {
      const capabilitiesJson = capabilityHelper.capabilitiesToJson(selectedCapabilities);

      let agentId = id || '';
      
      const diskThresholdsJson = JSON.stringify(diskThresholds);
      
      if (isEdit && id) {
        await agentService.update({
          id,
          // serverId ve tenantId değiştirilemez, sadece agent bilgileri güncellenir
          agentVersion: formData.agentVersion,
          osType: formData.osType,
          capabilities: capabilitiesJson,
          configVersion: formData.configVersion,
          heartbeatIntervalSeconds: formData.heartbeatIntervalSeconds,
          configPullIntervalSeconds: formData.configPullIntervalSeconds,
          healthCheckIntervalSeconds: formData.healthCheckIntervalSeconds,
          metricsCollectionIntervalSeconds: formData.metricsCollectionIntervalSeconds,
          heartbeatTimeoutMultiplier: formData.heartbeatTimeoutMultiplier,
          cpuWarningThreshold: formData.cpuWarningThreshold,
          cpuCriticalThreshold: formData.cpuCriticalThreshold,
          ramWarningThreshold: formData.ramWarningThreshold,
          ramCriticalThreshold: formData.ramCriticalThreshold,
          diskThresholds: diskThresholdsJson,
          isActive: formData.isActive,
          eStatementConnectionString: formData.eStatementConnectionString || undefined,
          eStatementSqlServerType: formData.eStatementSqlServerType,
          eStatementSonIslemZamaniIntervalSeconds: formData.eStatementSonIslemZamaniIntervalSeconds,
          eStatementSirketlerCekmeSikligiSeconds: formData.eStatementSirketlerCekmeSikligiSeconds,
          bankServiceRunIntervalSeconds: formData.bankServiceRunIntervalSeconds,
          eStatementDataProtectionEnabled: formData.eStatementDataProtectionEnabled,
          eStatementDataProtectionKeyPath: formData.eStatementDataProtectionKeyPath || undefined,
          runServiceAdminPasswd: formData.runServiceAdminPasswd || undefined,
        });
      } else {
        agentId = await agentService.create({
          serverId: formData.serverId,
          tenantId: formData.tenantId,
          agentVersion: formData.agentVersion,
          osType: formData.osType,
          capabilities: capabilitiesJson,
          configVersion: formData.configVersion,
          heartbeatIntervalSeconds: formData.heartbeatIntervalSeconds,
          configPullIntervalSeconds: formData.configPullIntervalSeconds,
          healthCheckIntervalSeconds: formData.healthCheckIntervalSeconds,
          metricsCollectionIntervalSeconds: formData.metricsCollectionIntervalSeconds,
          heartbeatTimeoutMultiplier: formData.heartbeatTimeoutMultiplier,
          cpuWarningThreshold: formData.cpuWarningThreshold,
          cpuCriticalThreshold: formData.cpuCriticalThreshold,
          ramWarningThreshold: formData.ramWarningThreshold,
          ramCriticalThreshold: formData.ramCriticalThreshold,
          diskThresholds: diskThresholdsJson,
          eStatementConnectionString: formData.eStatementConnectionString || undefined,
          eStatementSqlServerType: formData.eStatementSqlServerType,
          eStatementSonIslemZamaniIntervalSeconds: formData.eStatementSonIslemZamaniIntervalSeconds,
          eStatementSirketlerCekmeSikligiSeconds: formData.eStatementSirketlerCekmeSikligiSeconds,
          bankServiceRunIntervalSeconds: formData.bankServiceRunIntervalSeconds,
          eStatementDataProtectionEnabled: formData.eStatementDataProtectionEnabled,
          eStatementDataProtectionKeyPath: formData.eStatementDataProtectionKeyPath || undefined,
          runServiceAdminPasswd: formData.runServiceAdminPasswd || undefined,
        });
        
        // Oluşturulan AgentId'yi göster
        setCurrentAgentId(agentId);
        setCreatedAgentId(agentId);
      }

      // ApplicationDefinition'ları kaydet
      const allAppDefs: Array<{
        capability: AgentCapability;
        apps: CapabilityApplicationDefinition[];
      }> = [
        { capability: AgentCapability.IisMonitoring, apps: iisApps },
        { capability: AgentCapability.WindowsTaskMonitoring, apps: windowsTasks },
        { capability: AgentCapability.WindowsServiceMonitoring, apps: windowsServices },
        { capability: AgentCapability.LinuxCronMonitoring, apps: linuxCrons },
      ];

      // Önce mevcut ApplicationDefinition'ları topla (sadece edit modunda)
      let existingAppDefs: ApplicationDefinition[] = [];
      if (isEdit && id) {
        existingAppDefs = await applicationDefinitionService.getAll(formData.tenantId, formData.serverId);
        console.log('Mevcut ApplicationDefinition\'lar:', existingAppDefs);
      }

      // Form'daki tüm app ID'lerini topla (temp- ile başlamayanlar)
      const formAppIds = new Set<string>();
      for (const { apps } of allAppDefs) {
        apps.forEach((app) => {
          if (app.id && !app.id.startsWith('temp-')) {
            formAppIds.add(app.id);
          }
        });
      }
      console.log('Form\'daki app ID\'leri:', Array.from(formAppIds));

      // Seçili olmayan capability'lere ait ApplicationDefinition'ları sil
      const selectedAppTypes = new Set<ApplicationType>();
      for (const capability of selectedCapabilities) {
        const appType = capabilityHelper.getApplicationTypeForCapability(capability);
        if (appType) {
          selectedAppTypes.add(appType as ApplicationType);
        }
      }

      // Form'da olmayan veya seçili olmayan capability'lere ait ApplicationDefinition'ları sil (sadece edit modunda)
      if (isEdit && id) {
        for (const existingApp of existingAppDefs) {
          const appType = typeof existingApp.applicationType === 'number' 
            ? existingApp.applicationType as ApplicationType
            : (existingApp.applicationTypeName === 'IisApp' ? ApplicationType.IisApp
              : existingApp.applicationTypeName === 'WindowsTask' ? ApplicationType.WindowsTask
              : existingApp.applicationTypeName === 'WindowsService' ? ApplicationType.WindowsService
              : ApplicationType.LinuxCron);
          
          // Eğer form'da yoksa veya capability seçili değilse sil
          const shouldDelete = !formAppIds.has(existingApp.id) || !selectedAppTypes.has(appType);
          
          if (shouldDelete) {
            console.log('ApplicationDefinition siliniyor:', existingApp.id, existingApp.name, 'Sebep:', !formAppIds.has(existingApp.id) ? 'Form\'da yok' : 'Capability seçili değil');
            await applicationDefinitionService.delete(existingApp.id);
          }
        }
      }

      // ApplicationDefinition'ları kaydet/güncelle
      for (const { capability, apps } of allAppDefs) {
        if (!selectedCapabilities.includes(capability) || apps.length === 0) continue;

        const appType = capabilityHelper.getApplicationTypeForCapability(capability);
        if (!appType) continue;

        for (const app of apps) {
          // Validation: Her capability için gerekli alanları kontrol et
          let isValid = false;
          if (capability === AgentCapability.IisMonitoring) {
            // siteName ve appPoolName boş string olabilir (default değerler), sadece undefined/null kontrolü yap
            // Ancak name, expectedSiteState ve expectedAppPoolState zorunlu
            isValid = !!(app.name && 
              app.name.trim() !== '' &&
              app.siteName !== undefined && app.siteName !== null && 
              app.appPoolName !== undefined && app.appPoolName !== null && 
              app.expectedSiteState && app.expectedSiteState.trim() !== '' &&
              app.expectedAppPoolState && app.expectedAppPoolState.trim() !== '');
          } else if (capability === AgentCapability.WindowsTaskMonitoring) {
            isValid = !!(app.name && app.name.trim() !== '' && 
              app.taskName && app.taskName.trim() !== '' && 
              app.maxDelayMinutes && app.maxDelayMinutes > 0);
          } else if (capability === AgentCapability.WindowsServiceMonitoring) {
            isValid = !!(app.name && app.name.trim() !== '' && 
              app.serviceName && app.serviceName.trim() !== '' && 
              app.expectedStatus && app.expectedStatus.trim() !== '');
          } else if (capability === AgentCapability.LinuxCronMonitoring) {
            isValid = !!(app.name && app.name.trim() !== '' && 
              app.jobName && app.jobName.trim() !== '' && 
              app.schedule && app.schedule.trim() !== '' && 
              app.maxDelayMinutes && app.maxDelayMinutes > 0);
          }

          if (!isValid) {
            console.log('ApplicationDefinition geçersiz, atlanıyor:', {
              app,
              isValid,
              capability,
              name: app.name,
              ...(capability === AgentCapability.IisMonitoring && {
                siteName: app.siteName,
                appPoolName: app.appPoolName,
                expectedSiteState: app.expectedSiteState,
                expectedAppPoolState: app.expectedAppPoolState,
              }),
            });
            continue; // Boş/eksik satırları atla
          }

          let identifier: any;
          let healthRule: any;

          // CapabilityDocs V2'ye göre identifier ve healthRule oluştur
          if (capability === AgentCapability.IisMonitoring) {
            // siteName ve appPoolName değerlerini kontrol et - boş string bile geçerli
            const siteNameValue = app.siteName !== undefined && app.siteName !== null ? String(app.siteName) : '';
            const appPoolNameValue = app.appPoolName !== undefined && app.appPoolName !== null ? String(app.appPoolName) : '';
            
            console.log('IIS Identifier oluşturuluyor:', {
              siteName: siteNameValue,
              appPoolName: appPoolNameValue,
              appSiteName: app.siteName,
              appAppPoolName: app.appPoolName,
              appSiteNameType: typeof app.siteName,
              appAppPoolNameType: typeof app.appPoolName,
            });
            
            identifier = {
              siteName: siteNameValue,
              appPoolName: appPoolNameValue,
              expectedSiteState: app.expectedSiteState || 'Started',
              expectedAppPoolState: app.expectedAppPoolState || 'Running',
              httpMethod: app.httpMethod || 'GET',
              httpStatusCodeExpected: app.httpStatusCodeExpected || 200,
            };
            
            // Optional alanları sadece değer varsa ekle (boş string değilse)
            if (app.healthCheckUrl && app.healthCheckUrl.trim() !== '') {
              identifier.healthCheckUrl = app.healthCheckUrl;
            }
            if (app.responseTimeThresholdMs !== undefined && app.responseTimeThresholdMs !== null) {
              let thresholdValue: number | undefined;
              if (typeof app.responseTimeThresholdMs === 'number') {
                thresholdValue = app.responseTimeThresholdMs;
              } else if (typeof app.responseTimeThresholdMs === 'string') {
                const trimmed = app.responseTimeThresholdMs.trim();
                if (trimmed !== '') {
                  const parsed = Number(trimmed);
                  if (!isNaN(parsed)) {
                    thresholdValue = parsed;
                  }
                }
              }
              if (thresholdValue !== undefined) {
                identifier.responseTimeThresholdMs = thresholdValue;
              }
            }
            if (app.timeoutMs !== undefined && app.timeoutMs !== null) {
              let timeoutValue: number | undefined;
              if (typeof app.timeoutMs === 'number') {
                timeoutValue = app.timeoutMs;
              } else if (typeof app.timeoutMs === 'string') {
                const trimmed = app.timeoutMs.trim();
                if (trimmed !== '') {
                  const parsed = Number(trimmed);
                  if (!isNaN(parsed)) {
                    timeoutValue = parsed;
                  }
                }
              }
              if (timeoutValue !== undefined) {
                identifier.timeoutMs = timeoutValue;
              }
            }
            
            healthRule = { type: 'appPoolRunning' };
          } else if (capability === AgentCapability.WindowsTaskMonitoring) {
            identifier = {
              taskName: app.taskName || '',
              lastRunResultExpected: app.lastRunResultExpected || undefined,
              shouldBeRunning: app.shouldBeRunning || undefined,
            };
            // Undefined değerleri temizle
            Object.keys(identifier).forEach(key => {
              if (identifier[key] === undefined) {
                delete identifier[key];
              }
            });
            healthRule = {
              type: 'lastRun',
              maxDelayMinutes: app.maxDelayMinutes || 15,
            };
          } else if (capability === AgentCapability.WindowsServiceMonitoring) {
            identifier = {
              serviceName: app.serviceName || '',
              expectedStatus: app.expectedStatus || 'Running',
              autoRestartEnabled: app.autoRestartEnabled || undefined,
              restartAttemptCount: app.restartAttemptCount || undefined,
            };
            // Undefined değerleri temizle
            Object.keys(identifier).forEach(key => {
              if (identifier[key] === undefined) {
                delete identifier[key];
              }
            });
            healthRule = { type: 'serviceRunning' };
          } else if (capability === AgentCapability.LinuxCronMonitoring) {
            identifier = {
              jobName: app.jobName || '',
              schedule: app.schedule || '',
              lastExecutionStatusExpected: app.lastExecutionStatusExpected || undefined,
            };
            // Undefined değerleri temizle
            Object.keys(identifier).forEach(key => {
              if (identifier[key] === undefined) {
                delete identifier[key];
              }
            });
            healthRule = {
              type: 'lastRun',
              maxDelayMinutes: app.maxDelayMinutes || 15,
            };
          }

          console.log('ApplicationDefinition kaydediliyor:', {
            id: app.id,
            name: app.name,
            applicationType: appType,
            identifier: JSON.stringify(identifier, null, 2),
            healthRule: JSON.stringify(healthRule, null, 2),
            expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
            serverId: formData.serverId,
            tenantId: formData.tenantId,
            appData: {
              siteName: app.siteName,
              appPoolName: app.appPoolName,
              expectedSiteState: app.expectedSiteState,
              expectedAppPoolState: app.expectedAppPoolState,
            },
          });

          // Eğer app.id yoksa veya temp ile başlıyorsa yeni kayıt, değilse güncelleme
          if (!app.id || app.id.startsWith('temp-')) {
            // Yeni ApplicationDefinition oluştur
            try {
              if (!formData.serverId || !formData.tenantId) {
                throw new Error('ServerId ve TenantId gereklidir');
              }
              
              console.log('ApplicationDefinition oluşturuluyor:', {
                serverId: formData.serverId,
                tenantId: formData.tenantId,
                name: app.name,
                applicationType: appType,
                identifier: JSON.stringify(identifier),
                healthRule: JSON.stringify(healthRule),
                expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
              });
              
              const createdId = await applicationDefinitionService.create({
                serverId: formData.serverId,
                tenantId: formData.tenantId,
                name: app.name,
                applicationType: appType as ApplicationType,
                identifier,
                healthRule,
                expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
              });
              console.log('ApplicationDefinition oluşturuldu:', createdId);
            } catch (createErr: any) {
              console.error('ApplicationDefinition oluşturma hatası:', createErr, {
                name: app.name,
                identifier: JSON.stringify(identifier),
                healthRule: JSON.stringify(healthRule),
                requestData: {
                  serverId: formData.serverId,
                  tenantId: formData.tenantId,
                  name: app.name,
                  applicationType: appType,
                  identifier,
                  healthRule,
                  expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
                },
              });
              throw createErr;
            }
          } else {
            // Mevcut ApplicationDefinition'ı güncelle
            try {
              console.log('ApplicationDefinition güncelleniyor:', {
                id: app.id,
                name: app.name,
                applicationType: appType,
                identifier: JSON.stringify(identifier),
                healthRule: JSON.stringify(healthRule),
                expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
              });
              
              // Backend'de ServerId ve TenantId güncellenmiyor, sadece Id kullanılıyor
              await applicationDefinitionService.update({
                id: app.id,
                serverId: formData.serverId, // Backend'de kullanılmıyor ama type'da var
                tenantId: formData.tenantId, // Backend'de kullanılmıyor ama type'da var
                name: app.name,
                applicationType: appType as ApplicationType,
                identifier,
                healthRule,
                expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
              });
              console.log('ApplicationDefinition güncellendi:', app.id);
            } catch (updateErr: any) {
              console.error('ApplicationDefinition güncelleme hatası:', updateErr, {
                id: app.id,
                name: app.name,
                identifier: JSON.stringify(identifier),
                healthRule: JSON.stringify(healthRule),
                requestData: {
                  id: app.id,
                  name: app.name,
                  applicationType: appType,
                  identifier,
                  healthRule,
                  expectedRunIntervalMinutes: app.expectedRunIntervalMinutes,
                },
              });
              throw updateErr;
            }
          }
        }
      }

      // Eğer yeni agent oluşturulduysa, edit moduna geç ve AgentId'yi göster
      if (!isEdit && agentId) {
        // URL'i güncelle ama sayfayı yenileme (state korunur)
        window.history.replaceState(null, '', `/agents/${agentId}/edit`);
        // Form'u edit moduna al
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('agentId', agentId);
        // AgentId'yi göster ve kullanıcıya bilgi ver
        setError(null);
        // ApplicationDefinition'lar kaydedildi, sadece AgentId göster
      } else {
        navigate('/agents');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/agents');
  };

  const handleCopyAgentId = async () => {
    if (!currentAgentId) return;
    try {
      await navigator.clipboard.writeText(currentAgentId);
      setCopiedAgentId(true);
      setTimeout(() => setCopiedAgentId(false), 2000);
    } catch (err) {
      console.error('Kopyalama hatası:', err);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {isEdit ? 'Agent Düzenle' : 'Yeni Agent Oluştur'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <Box ref={formRef} component="form" onSubmit={handleSubmit}>
            {(isEdit || createdAgentId) && currentAgentId && (
              <Card 
                sx={{ 
                  mb: 2.5, 
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                  borderRadius: 2,
                  boxShadow: 'none'
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Agent ID
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleCopyAgentId}
                      color={copiedAgentId ? 'success' : 'default'}
                      title="Agent ID'yi kopyala"
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    bgcolor: 'background.paper',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        flex: 1,
                        fontSize: '0.875rem',
                        wordBreak: 'break-all'
                      }}
                    >
                      {currentAgentId}
                    </Typography>
                    {copiedAgentId && (
                      <Chip 
                        label="Kopyalandı!" 
                        size="small" 
                        color="success"
                        sx={{ height: 24 }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Bu Agent ID'yi sunucudaki appsettings.json dosyasına ekleyin.
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Box sx={{ mb: 2, mt: 2 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab label="Temel Bilgiler" />
                <Tab label="Interval Ayarları" />
                <Tab label="Özellikler" />
                <Tab label="eStatement Bağlantı Ayarları" />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ pt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Agent'ın temel bilgilerini buradan yapılandırabilirsiniz.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {!isEdit && (
                      <>
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                          <FormControl fullWidth size="small" required>
                            <InputLabel>Şirket Grubu</InputLabel>
                            <Select
                              value={formData.tenantId}
                              onChange={(e) => setFormData({ ...formData, tenantId: e.target.value, serverId: '' })}
                              label="Şirket Grubu"
                            >
                              {tenants.map((tenant) => (
                                <MenuItem key={tenant.id} value={tenant.id}>
                                  {tenant.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                          <FormControl fullWidth size="small" required>
                            <InputLabel>Sunucu</InputLabel>
                            <Select
                              value={formData.serverId}
                              onChange={(e) => setFormData({ ...formData, serverId: e.target.value })}
                              label="Sunucu"
                              disabled={!formData.tenantId || filteredServers.length === 0}
                            >
                              {filteredServers.map((server) => (
                                <MenuItem key={server.id} value={server.id}>
                                  {server.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </>
                    )}

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Agent Versiyonu"
                        value={formData.agentVersion}
                        onChange={(e) => setFormData({ ...formData, agentVersion: e.target.value })}
                        placeholder="Örn: 1.0.0"
                        helperText="Agent yazılımının versiyonu (örn: 1.0.0)"
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>İşletim Sistemi</InputLabel>
                        <Select
                          value={formData.osType.toString()}
                          onChange={(e) => setFormData({ ...formData, osType: Number(e.target.value) as OSType })}
                          label="İşletim Sistemi"
                        >
                          <MenuItem value={OSType.Windows.toString()}>Windows</MenuItem>
                          <MenuItem value={OSType.Linux.toString()}>Linux</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {isEdit && (
                      <Box sx={{ width: '100%' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.isActive}
                              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                              color="primary"
                            />
                          }
                          label="Agent Aktif"
                        />
                      </Box>
                    )}

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        type="number"
                        label="Config Versiyonu"
                        value={formData.configVersion}
                        onChange={(e) => setFormData({ ...formData, configVersion: Number(e.target.value) })}
                        inputProps={{ min: 1 }}
                        helperText="Agent konfigürasyon versiyonu. Konfigürasyon değiştiğinde bu değer artırılır ve agent yeni konfigürasyonu çeker."
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ pt: 2 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Agent Interval Ayarları Hakkında
                    </Typography>
                    <Typography variant="body2" component="div">
                      Bu ayarlar agent'ın çalışma davranışını kontrol eder. Her interval, agent'ın belirli bir işlemi ne sıklıkla yapacağını belirler.
                      <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li><strong>Heartbeat Interval:</strong> Agent'ın merkeze "çalışıyorum" sinyali gönderme sıklığı. Bu sinyal, agent'ın online/offline durumunu belirlemek için kullanılır.</li>
                        <li><strong>Config Pull Interval:</strong> Agent'ın merkezden konfigürasyon (uygulama tanımları, threshold'lar vb.) çekme sıklığı.</li>
                        <li><strong>Health Check Interval:</strong> Agent'ın uygulamaları (IIS, Windows Service, Task, Cron) kontrol etme sıklığı.</li>
                        <li><strong>Metrics Collection Interval:</strong> Agent'ın sistem metriklerini (CPU, RAM, Disk) toplama sıklığı.</li>
                        <li><strong>Heartbeat Timeout Multiplier:</strong> Agent'ın offline sayılması için geçmesi gereken süreyi hesaplamak için kullanılır. Timeout = Heartbeat Interval × Multiplier</li>
                      </ul>
                    </Typography>
                  </Alert>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Heartbeat Interval (Saniye)"
                        value={formData.heartbeatIntervalSeconds}
                        onChange={(e) => setFormData({ ...formData, heartbeatIntervalSeconds: parseInt(e.target.value) || 60 })}
                        helperText="Agent'ın merkeze 'çalışıyorum' sinyali gönderme sıklığı. En az 60 saniye. Varsayılan: 60 saniye"
                        inputProps={{ min: 60, max: 3600 }}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Config Pull Interval (Saniye)"
                        value={formData.configPullIntervalSeconds}
                        onChange={(e) => setFormData({ ...formData, configPullIntervalSeconds: parseInt(e.target.value) || 60 })}
                        helperText="Agent'ın merkezden konfigürasyon çekme sıklığı. En az 60 saniye. Varsayılan: 60 saniye"
                        inputProps={{ min: 60, max: 3600 }}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Health Check Interval (Saniye)"
                        value={formData.healthCheckIntervalSeconds}
                        onChange={(e) => setFormData({ ...formData, healthCheckIntervalSeconds: parseInt(e.target.value) || 60 })}
                        helperText="Agent'ın uygulamaları kontrol etme sıklığı. En az 60 saniye. Varsayılan: 60 saniye"
                        inputProps={{ min: 60, max: 3600 }}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Metrics Collection Interval (Saniye)"
                        value={formData.metricsCollectionIntervalSeconds}
                        onChange={(e) => setFormData({ ...formData, metricsCollectionIntervalSeconds: parseInt(e.target.value) || 60 })}
                        helperText="Agent'ın sistem metriklerini toplama sıklığı. En az 60 saniye. Varsayılan: 60 saniye"
                        inputProps={{ min: 60, max: 3600 }}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Heartbeat Timeout Çarpanı"
                        value={formData.heartbeatTimeoutMultiplier}
                        onChange={(e) => setFormData({ ...formData, heartbeatTimeoutMultiplier: parseFloat(e.target.value) || 2.0 })}
                        helperText={`Agent'ın offline sayılması için geçmesi gereken süre: ${formData.heartbeatIntervalSeconds} × ${formData.heartbeatTimeoutMultiplier} = ${(formData.heartbeatIntervalSeconds * formData.heartbeatTimeoutMultiplier).toFixed(1)} saniye. Varsayılan: 2.0`}
                        inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                        required
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ pt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    eStatement için veritabanı bağlantı ayarlarını buradan yapılandırabilirsiniz. Agent bu ayarları kullanarak SQL sorgusunu çalıştırır ve sonuçları merkeze iletir.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Connection String"
                      value={formData.eStatementConnectionString}
                      onChange={(e) => setFormData((prev) => ({ ...prev, eStatementConnectionString: e.target.value }))}
                      placeholder={formData.eStatementDataProtectionEnabled ? "Protect edilmiş (hash'li) connection string yapıştırın" : "Server=localhost;Database=db;User Id=user;Password=pass;"}
                      multiline
                      rows={3}
                      helperText={formData.eStatementDataProtectionEnabled
                        ? "Data Protection açıksa buraya kendi ortamınızda Protect edilmiş connection string'i yapıştırın. Agent Key Path ile Unprotect edecek."
                        : "Veritabanı bağlantı string'i. Örnek: Server=localhost;Database=db;User Id=user;Password=pass;"}
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.eStatementDataProtectionEnabled}
                          onChange={(e) => setFormData((prev) => ({ ...prev, eStatementDataProtectionEnabled: e.target.checked }))}
                          aria-label="Data Protection kullan"
                        />
                      }
                      label="Connection string Data Protection ile korunmuş (hash'li) olarak girilecek"
                    />
                    {formData.eStatementDataProtectionEnabled && (
                      <TextField
                        label="Data Protection Key Path"
                        value={formData.eStatementDataProtectionKeyPath}
                        onChange={(e) => setFormData((prev) => ({ ...prev, eStatementDataProtectionKeyPath: e.target.value }))}
                        placeholder="D:\eHesApp\config"
                        helperText="Yukarıdaki connection string bu key path ile Protect edilmiş olmalı. Agent, bu path'e bakarak Unprotect edip eStatement DB'ye bağlanır."
                        fullWidth
                      />
                    )}
                    <FormControl fullWidth>
                      <InputLabel>SQL Server Tipi</InputLabel>
                      <Select
                        value={formData.eStatementSqlServerType !== undefined ? String(formData.eStatementSqlServerType) : ''}
                        label="SQL Server Tipi"
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ 
                            ...prev, 
                            eStatementSqlServerType: value === '' ? undefined : (Number(value) as SqlServerType)
                          }));
                        }}
                      >
                        <MenuItem value="">Seçiniz</MenuItem>
                        <MenuItem value={String(SqlServerType.MSSQL)}>MSSQL</MenuItem>
                        <MenuItem value={String(SqlServerType.POSTGRESQL)}>PostgreSQL</MenuItem>
                        <MenuItem value={String(SqlServerType.MYSQL)}>MySQL</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Son İşlem Zamanı Çekme Sıklığı (Saniye)"
                      type="number"
                      value={formData.eStatementSonIslemZamaniIntervalSeconds || ''}
                      onChange={(e) => setFormData((prev) => ({ 
                        ...prev, 
                        eStatementSonIslemZamaniIntervalSeconds: e.target.value ? parseInt(e.target.value, 10) : undefined 
                      }))}
                      placeholder="300"
                      helperText="Agent'ın DB'den veri çekme sıklığı (saniye). En az 60. Örnek: 300 = 5 dakikada bir"
                      fullWidth
                      inputProps={{ min: 60 }}
                    />
                    <TextField
                      label="Şirketler / Şirket Hesap Çekme Sıklığı (Saniye)"
                      type="number"
                      value={formData.eStatementSirketlerCekmeSikligiSeconds ?? ''}
                      onChange={(e) => setFormData((prev) => ({ 
                        ...prev, 
                        eStatementSirketlerCekmeSikligiSeconds: e.target.value ? parseInt(e.target.value, 10) : undefined 
                      }))}
                      placeholder="300"
                      helperText="Agent'ın Sirketler ve SirketHesap tablolarını merkeze gönderme sıklığı (saniye). En az 60. Boş bırakılırsa şirket senkronu çalışmaz."
                      fullWidth
                      inputProps={{ min: 60 }}
                    />
                    <TextField
                      label="Banka Servis Tetikleme Sıklığı (Saniye)"
                      type="number"
                      value={formData.bankServiceRunIntervalSeconds ?? ''}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        bankServiceRunIntervalSeconds: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      }))}
                      placeholder="60"
                      helperText="Kuyruktaki bekleyen banka servis tetiklemelerini bu aralıkla çalıştırır (saniye). En az 60. Boş bırakılırsa kuyruk işlenmez."
                      fullWidth
                      inputProps={{ min: 60 }}
                      inputRef={bankServiceRunIntervalSecondsInputRef}
                    />
                    <TextField
                      label="Run Service Admin Passwd"
                      type="password"
                      value={formData.runServiceAdminPasswd ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, runServiceAdminPasswd: e.target.value }))}
                      placeholder="Request'i dahil et aktifken -w ile gönderilir"
                      helperText="Banka servis tetiklemede 'Request'i dahil et' işaretliyse bu değer -w parametresi ile runservice'e gönderilir."
                      fullWidth
                      autoComplete="off"
                    />
                  </Box>
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ pt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                      Özellikler ve Uygulama Tanımları <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {capabilityHelper.getCapabilitiesForOS(formData.osType).map((capability) => {
                  const supportedTypes = capabilityHelper.getSupportedApplicationTypes([capability]);
                  const isSelected = selectedCapabilities.includes(capability);
                  const hasAppDefinitions = capability === AgentCapability.IisMonitoring
                    ? iisApps.length > 0
                    : capability === AgentCapability.WindowsTaskMonitoring
                    ? windowsTasks.length > 0
                    : capability === AgentCapability.WindowsServiceMonitoring
                    ? windowsServices.length > 0
                    : capability === AgentCapability.LinuxCronMonitoring
                    ? linuxCrons.length > 0
                    : false;

                  // CPU, RAM ve Disk monitoring için accordion'u otomatik aç (application definition gerektirmezler)
                  const shouldAutoExpand = isSelected && (
                    supportedTypes.length > 0 || 
                    capability === AgentCapability.CpuMonitoring ||
                    capability === AgentCapability.MemoryMonitoring ||
                    capability === AgentCapability.DiskMonitoring
                  );

                  return (
                    <Accordion 
                      key={capability} 
                      expanded={shouldAutoExpand}
                      sx={{ 
                        '&:before': { display: 'none' },
                        border: isSelected ? '1px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          minHeight: 48,
                          '&.Mui-expanded': { minHeight: 48 },
                          bgcolor: isSelected ? 'primary.50' : 'transparent',
                          '&:hover': { bgcolor: isSelected ? 'primary.100' : 'action.hover' }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCapabilities([...selectedCapabilities, capability]);
                                } else {
                                  setSelectedCapabilities(
                                    selectedCapabilities.filter((c) => c !== capability)
                                  );
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 500 }}>
                                {capabilityHelper.getCapabilityLabel(capability)}
                              </Typography>
                              <InfoIcon text={getCapabilityDescription(capability)} />
                              {isSelected && hasAppDefinitions && (
                                <Chip
                                  size="small"
                                  label={
                                    capability === AgentCapability.IisMonitoring
                                      ? `${iisApps.length} uygulama`
                                      : capability === AgentCapability.WindowsTaskMonitoring
                                      ? `${windowsTasks.length} uygulama`
                                      : capability === AgentCapability.WindowsServiceMonitoring
                                      ? `${windowsServices.length} uygulama`
                                      : `${linuxCrons.length} uygulama`
                                  }
                                  color="primary"
                                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          sx={{ margin: 0 }}
                        />
                      </AccordionSummary>
                      {isSelected && (
                        <AccordionDetails sx={{ pt: 2 }}>
                          {capability === AgentCapability.CpuMonitoring && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                CPU Threshold'ları
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Warning Threshold (%)"
                                    value={formData.cpuWarningThreshold ?? ''}
                                    onChange={(e) => setFormData({ ...formData, cpuWarningThreshold: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                    helperText="Varsayılan: 80"
                                  />
                                </Box>
                                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Critical Threshold (%)"
                                    value={formData.cpuCriticalThreshold ?? ''}
                                    onChange={(e) => setFormData({ ...formData, cpuCriticalThreshold: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                    helperText="Varsayılan: 90"
                                  />
                                </Box>
                              </Box>
                            </Box>
                          )}
                          {capability === AgentCapability.MemoryMonitoring && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                RAM Threshold'ları
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Warning Threshold (%)"
                                    value={formData.ramWarningThreshold ?? ''}
                                    onChange={(e) => setFormData({ ...formData, ramWarningThreshold: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                    helperText="Varsayılan: 80"
                                  />
                                </Box>
                                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Critical Threshold (%)"
                                    value={formData.ramCriticalThreshold ?? ''}
                                    onChange={(e) => setFormData({ ...formData, ramCriticalThreshold: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                    helperText="Varsayılan: 90"
                                  />
                                </Box>
                              </Box>
                            </Box>
                          )}
                          {capability === AgentCapability.DiskMonitoring && (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Disk Threshold'ları
                                </Typography>
                                <Button
                                  startIcon={<AddIcon />}
                                  variant="outlined"
                                  size="small"
                                  onClick={() => setDiskThresholds([...diskThresholds, { drive: '', warning: 70, critical: 90 }])}
                                >
                                  Disk Ekle
                                </Button>
                              </Box>
                              {diskThresholds.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                  İzlemek istediğiniz diskleri ekleyin
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {diskThresholds.map((threshold, index) => (
                                  <Paper 
                                    key={index} 
                                    sx={{ 
                                      p: 1.5, 
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(25% - 12px)' } }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Disk Sürücüsü"
                                          value={threshold.drive}
                                          onChange={(e) => {
                                            const newThresholds = [...diskThresholds];
                                            newThresholds[index].drive = e.target.value;
                                            setDiskThresholds(newThresholds);
                                          }}
                                          placeholder="C:"
                                        />
                                      </Box>
                                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(35% - 12px)' } }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          label="Warning (%)"
                                          value={threshold.warning}
                                          onChange={(e) => {
                                            const newThresholds = [...diskThresholds];
                                            newThresholds[index].warning = parseFloat(e.target.value) || 0;
                                            setDiskThresholds(newThresholds);
                                          }}
                                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                                        />
                                      </Box>
                                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(35% - 12px)' }, display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          label="Critical (%)"
                                          value={threshold.critical}
                                          onChange={(e) => {
                                            const newThresholds = [...diskThresholds];
                                            newThresholds[index].critical = parseFloat(e.target.value) || 0;
                                            setDiskThresholds(newThresholds);
                                          }}
                                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                                        />
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => setDiskThresholds(diskThresholds.filter((_, i) => i !== index))}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  </Paper>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {supportedTypes.length > 0 && renderApplicationDefinitionForm(capability)}
                        </AccordionDetails>
                      )}
                    </Accordion>
                  );
                })}
                {selectedCapabilities.length === 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                    En az bir capability seçmelisiniz
                  </Typography>
                )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>


            <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                size="medium"
              >
                İptal
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                size="medium"
              >
                {loading ? <CircularProgress size={24} /> : isEdit ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Agent versiyonu artırma hatırlatması */}
      <Dialog
        open={versionReminderDialogOpen}
        onClose={() => setVersionReminderDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="version-reminder-dialog-title"
        aria-describedby="version-reminder-dialog-description"
      >
        <DialogTitle id="version-reminder-dialog-title">
          Agent versiyonu
        </DialogTitle>
        <DialogContent>
          <Typography id="version-reminder-dialog-description">
            Değişiklik yaptınız. Agent versiyonunu artırmanız önerilir; böylece agent yeni yapılandırmayı çekecektir. Yine de güncellemek istiyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionReminderDialogOpen(false)} color="inherit">
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setVersionReminderDialogOpen(false);
              skipVersionReminderCheckRef.current = true;
              formRef.current?.requestSubmit();
            }}
            autoFocus
          >
            Yine de Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capability Application Definition Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingApp && editingApp.id && !editingApp.id.startsWith('temp-')
            ? 'Uygulama Tanımını Düzenle'
            : 'Yeni Uygulama Tanımı Ekle'}
        </DialogTitle>
        <DialogContent>
          {renderDialogForm()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSaveApp} variant="contained" color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default AgentForm;
